import { useRef } from "react";
import { useParams } from "react-router";
import { useScroll, useTransform } from "motion/react";
import cx from "classnames";
import { useGlobalContext } from "@utils/GlobalContext";
import { useTheme } from "@hooks/useTheme";
import { ResultOf } from "gql.tada";
import { DescriptionFillSectionById } from "@queries/entries/DescriptionFillSectionById";
import { AnimatedSplitLines } from "@components/shared/AnimatedSplitLines";
import { Button } from "@components/shared/core/Button";
import { Link } from "@components/shared/core/Link";
import { MarkdownLite } from "@components/shared/MarkdownLite";
import { TextBlockRichText } from "@components/shared/core/TextBlockRichText";
import { Analytics } from "@utils/Analytics";

export const DescriptionFillSectionWithCta = ({
  entry: { descriptionFillSection: entry },
  cta
}: {
  entry: ResultOf<typeof DescriptionFillSectionById>;
  cta: any,
}) => {
  const { locale } = useGlobalContext();
  const { slug } = useParams();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const value = useTransform(scrollYProgress, [0, 0.7], [-1, 0]);
  const { theme } = useTheme();
  const isNeutralForeground = theme.foreground.startsWith("neutral-");

  // if a gaTag is defined in contentful use it, or fallback to the original
  const handleCtaClick = (gaTag: string | undefined) => {
    if (gaTag) {
      Analytics.trackEvent(`${gaTag}_${locale}`);

    } else {
      // legacy hard-coded version
      Analytics.trackEvent(`${slug}_${locale}_cta_click`);
    }
  };

  if (!entry) return null;

  return (
    <div className="flex dsk:flex-row flex-col pt-[60px] pb-[60px] bg-palette-current-true" ref={sectionRef}>
      <div className="grid-container">
        <h1 className="dsk:col-start-1 col-span-7 dsk:mb-0 mb-10 text-palette-current-highlight font-['Netflix_Sans_Title'] font-bold text-[60px] dsk:text-[100px] leading-[60px] dsk:leading-[100px]">
          <MarkdownLite>{entry.title}</MarkdownLite>
        </h1>
        <div
          className={cx(
            "dsk:col-start-11 col-span-full",
            isNeutralForeground
              ? "text-palette-current-foreground"
              : "text-palette-current-highlight"
          )}
        >
          <p className="text-body-regular font-black text-[35px] leading-[40px] dsk:text-[45px] dsk:leading-[50px]">
            <AnimatedSplitLines
              value={value}
              animationType="clipPathReveal"
              underlyingTextClassNames={
                isNeutralForeground ? "opacity-30" : "text-palette-current-deep"
              }
            >
              <TextBlockRichText json={entry.descriptionRichText?.json} />
            </AnimatedSplitLines>
          </p>

          {cta &&
            <Button
              asChild
              variant="red-mx"
              size="large"
              style={{ marginTop: 100 }}
            >
              <Link link={cta} onClick={() => handleCtaClick(cta.gaTag)}>{cta.text}</Link>
            </Button>
          }
        </div>
      </div>
    </div>
  );
};
